export interface GuideCard {
  title: string;
  description: string;
}

export const guideCards: GuideCard[] = [
  {
    title: 'Comprendé lo que estás sintiendo',
    description:
      'Empezá a observar las señales emocionales y energéticas que aparecen en tu vida cotidiana sin juzgarte ni exigirte tener todas las respuestas.',
  },
  {
    title: 'Reconocé qué necesitás',
    description:
      'Encontrá una orientación más clara para distinguir si necesitás hacer una pausa, recuperar calma, proteger tus límites, soltar o volver a enfocarte.',
  },
  {
    title: 'Elegí una práctica concreta',
    description:
      'Accedé a propuestas sencillas que podés utilizar como punto de partida según el momento que estés atravesando.',
  },
  {
    title: 'Avanzá a tu propio ritmo',
    description:
      'Recorré la guía de principio a fin o consultá directamente la sección que necesites. No hay una única manera correcta de utilizarla.',
  },
  {
    title: 'Creá un momento para vos',
    description:
      'Transformá la intención de cuidarte en una acción posible, sin rutinas largas ni objetivos difíciles de sostener.',
  },
  {
    title: 'Volvé cada vez que lo necesites',
    description:
      'Utilizá la guía como un recurso de consulta para diferentes momentos de tu proceso personal.',
  },
];

export interface Bono {
  badge: string;
  title: string;
  pages: string;
  description: string;
  highlight: string;
  /** Portada oficial del bono. Vacío hasta que se cargue el mockup real. */
  coverImage?: string;
}

export const bonos: Bono[] = [
  {
    badge: 'BONO 1',
    title: 'Planificador de Autocuidado',
    pages: '3 páginas',
    description:
      'Un recurso práctico para organizar pequeños momentos de autocuidado, definir qué necesitás priorizar y convertir tus intenciones en acciones posibles.',
    highlight: 'Planificá sin exigirte una rutina perfecta.',
    coverImage:
      'https://cdn.magicpatterns.com/uploads/3D4uVpD4BA4hZfHoBVN6DV/Bono-1-Planificador-de-Autocuidado-v2.webp',
  },
  {
    badge: 'BONO 2',
    title: 'Calendario Lunar',
    pages: '6 páginas',
    description:
      'Una guía visual para conocer las principales fases de la Luna y utilizarlas como inspiración para tus momentos de reflexión, intención y cierre.',
    highlight: 'Conectá tus prácticas con cada etapa del ciclo lunar.',
    coverImage:
      'https://cdn.magicpatterns.com/uploads/cfUWF77wa1XpVSqXPRaVwT/Bono-2-Calendario-Lunar.webp',
  },
  {
    badge: 'BONO 3',
    title: 'Limpieza Energética del Hogar',
    pages: '6 páginas',
    description:
      'Una guía sencilla para crear un ritual de limpieza energética y renovar la sensación de armonía dentro de tus espacios cotidianos.',
    highlight: 'Transformá tu hogar en un espacio más consciente.',
    coverImage:
      'https://cdn.magicpatterns.com/uploads/g3FTTjaMnUxhK8zd7LXagB/Bono-3-Limpieza-Energetica-del-Hogar.webp',
  },
  {
    badge: 'BONO 4',
    title: 'Diccionario de Cristales',
    pages: '12 páginas',
    description:
      'Un recurso visual para conocer diferentes cristales, sus asociaciones tradicionales y las formas en las que podés incorporarlos a tus prácticas personales.',
    highlight: 'Elegí el cristal que mejor acompañe tu intención.',
    coverImage:
      'https://cdn.magicpatterns.com/uploads/rUfA4T4b49aEh2bv5NRUHk/Bono-4-Diccionario-de-Cristales.webp',
  },
  {
    badge: 'BONO 5',
    title: 'Ritual de Protección',
    pages: '4 páginas',
    description:
      'Una práctica guiada para crear un momento de protección energética, fortalecer tus límites y conectar con una mayor sensación de resguardo personal.',
    highlight: 'Un ritual para volver a tu centro y cuidar tu espacio.',
    coverImage:
      'https://cdn.magicpatterns.com/uploads/3scPKudqFRE6WSiBUAcZek/Bono-5-Ritual-de-Proteccion.webp',
  },
  {
    badge: 'BONO 6',
    title: '50 Afirmaciones',
    pages: '5 páginas',
    description:
      'Una selección de afirmaciones organizadas para acompañar distintos momentos, reforzar intenciones y crear pausas conscientes durante el día.',
    highlight: 'Elegí una frase y convertíla en tu recordatorio diario.',
    coverImage:
      'https://cdn.magicpatterns.com/uploads/6BePN3AZY2SzCUhBkPwTsv/Bono-6-50-Afirmaciones.webp',
  },
  {
    badge: 'BONO 7',
    title: 'Respirá en 5 Minutos',
    pages: '4 páginas',
    description:
      'Ejercicios breves de respiración para hacer una pausa, bajar el ritmo y recuperar presencia cuando necesitás volver a vos.',
    highlight: 'Cinco minutos pueden convertirse en un nuevo comienzo.',
    coverImage:
      'https://cdn.magicpatterns.com/uploads/vLLswcj9voMkJSrk7hYzNv/Bono-7-Respira-en-5-Minutos.webp',
  },
  {
    badge: 'BONO 8',
    title: 'Diario de Bienestar de 30 Días',
    pages: '8 páginas',
    description:
      'Un diario guiado para registrar durante 30 días cómo te sentís, qué prácticas realizás y qué cambios comenzás a observar en tu experiencia.',
    highlight: 'Observá tu proceso y reconocé tus pequeños avances.',
    coverImage:
      'https://cdn.magicpatterns.com/uploads/699D98WqexQDKopXmVub9n/Bono-8-Diario-de-Bienestar-30-Dias.webp',
  },
];

export interface Faq {
  question: string;
  answer: string;
}

export const faqs: Faq[] = [
  {
    question: '¿Qué es Aura Viva 2026?',
    answer:
      'Es un sistema digital visual de autocuidado y bienestar energético compuesto por una guía principal, un documento de orientación y ocho bonos complementarios. Fue creado para ayudarte a reconocer qué estás atravesando, elegir una herramienta y comenzar con una práctica concreta.',
  },
  {
    question: '¿Qué recibo con mi compra?',
    answer:
      'Recibís 10 archivos digitales y 97 páginas en total: la Guía de Autocuración Energética, el documento “Empezá acá” y ocho bonos: Planificador de Autocuidado, Calendario Lunar, Limpieza Energética del Hogar, Diccionario de Cristales, Ritual de Protección, 50 Afirmaciones, Respirá en 5 Minutos y Diario de Bienestar de 30 Días.',
  },
  {
    question: '¿Es un producto físico?',
    answer:
      'No. Aura Viva 2026 es un producto 100 % digital. No se envía ningún libro, caja ni paquete por correo.',
  },
  {
    question: '¿Cómo recibo los materiales?',
    answer:
      'Después de confirmar el pago recibirás las indicaciones necesarias para acceder a los archivos digitales. Asegurate de ingresar correctamente tu correo y revisar también las carpetas de spam, correo no deseado o promociones.',
  },
  {
    question: '¿Necesito conocimientos previos?',
    answer:
      'No. El contenido fue creado para que puedas comenzar aunque nunca hayas realizado prácticas energéticas. El documento “Empezá acá” te orientará para elegir tu primer recurso.',
  },
  {
    question: '¿Tengo que usar todos los materiales?',
    answer:
      'No. Podés comenzar por una sola práctica o por el recurso que más se relacione con tu momento actual. No necesitás leer las 97 páginas antes de empezar.',
  },
  {
    question: '¿Cuánto tiempo necesito?',
    answer:
      'Depende de la práctica que elijas. Algunas propuestas fueron pensadas para realizarse en pocos minutos y otras pueden acompañarte durante varios días. Podés adaptar la experiencia a tu disponibilidad.',
  },
  {
    question: '¿Puedo acceder desde cualquier país?',
    answer: 'Sí. Aura Viva 2026 puede adquirirse desde cualquier país de Latinoamérica.',
  },
  {
    question: '¿El precio se cobra en mi moneda?',
    answer:
      'El precio de referencia es de USD 12. Antes de confirmar la compra, la página realiza automáticamente la conversión a tu moneda local para que puedas visualizar el importe correspondiente.',
  },
  {
    question: '¿Es una suscripción?',
    answer:
      'No. Realizás un único pago de USD 12. No existen mensualidades ni cobros recurrentes.',
  },
  {
    question: '¿Cuánto tiempo tengo acceso?',
    answer:
      'El acceso es de por vida. Podés guardar los materiales y volver a consultarlos cuando lo necesites.',
  },
  {
    question: '¿Puedo utilizarlo desde el celular?',
    answer:
      'Sí. Los archivos digitales pueden abrirse desde celular, tablet o computadora mediante una aplicación compatible con documentos PDF.',
  },
  {
    question: '¿Puedo imprimir los materiales?',
    answer:
      'Sí. Si lo preferís, podés imprimir las páginas que quieras completar a mano, como el planificador o el diario. También podés consultar los materiales directamente desde un dispositivo.',
  },
  {
    question: '¿Aura Viva 2026 reemplaza una terapia o tratamiento médico?',
    answer:
      'No. Aura Viva 2026 es un recurso educativo de bienestar y autocuidado. No diagnostica, trata ni reemplaza la atención médica, psicológica o de otros profesionales de la salud.',
  },
  {
    question: '¿Los resultados son iguales para todas las personas?',
    answer:
      'No. Cada experiencia es personal y puede variar. El material ofrece herramientas de autocuidado, pero no garantiza resultados emocionales, energéticos, físicos ni médicos específicos.',
  },
  {
    question: '¿Qué sucede si tengo problemas para acceder?',
    answer:
      'Podés comunicarte con nuestro equipo de soporte indicando el correo utilizado durante la compra. También te recomendamos revisar las carpetas de spam, correo no deseado y promociones.',
  },
  {
    question: '¿Cómo funciona la garantía?',
    answer:
      'Tu compra cuenta con una garantía de 7 días desde la fecha de adquisición. Si dentro de ese período considerás que el producto no es para vos, podés comunicarte con nuestro equipo para solicitar la aplicación de la garantía.',
  },
];

export interface ValueRow {
  kind: string;
  label: string;
  description: string;
  featured?: boolean;
}

export const valueRows: ValueRow[] = [
  {
    kind: 'Producto principal',
    label: 'Guía de Autocuración Energética',
    description:
      '48 páginas de orientación, prácticas y herramientas para ayudarte a reconocer qué estás atravesando y comenzar con una acción de autocuidado.',
    featured: true,
  },
  {
    kind: 'Recurso de bienvenida',
    label: 'Empezá acá',
    description:
      'Una guía breve para comprender cómo recorrer el material, qué abrir primero y cómo comenzar sin sentirte abrumada.',
    featured: true,
  },
  {
    kind: 'Bono 1',
    label: 'Planificador de Autocuidado',
    description: '3 páginas para organizar pequeños momentos de cuidado personal.',
  },
  {
    kind: 'Bono 2',
    label: 'Calendario Lunar',
    description:
      '6 páginas para conocer las fases lunares y acompañar tus momentos de intención y reflexión.',
  },
  {
    kind: 'Bono 3',
    label: 'Limpieza Energética del Hogar',
    description: '6 páginas para realizar un ritual consciente dentro de tus espacios.',
  },
  {
    kind: 'Bono 4',
    label: 'Diccionario de Cristales',
    description: '12 páginas para conocer distintos cristales y sus asociaciones tradicionales.',
  },
  {
    kind: 'Bono 5',
    label: 'Ritual de Protección',
    description:
      '4 páginas para crear un momento de protección energética y fortalecer tus límites.',
  },
  {
    kind: 'Bono 6',
    label: '50 Afirmaciones',
    description: '5 páginas de afirmaciones para acompañar diferentes intenciones y momentos.',
  },
  {
    kind: 'Bono 7',
    label: 'Respirá en 5 Minutos',
    description: '4 páginas de ejercicios breves para hacer una pausa y recuperar presencia.',
  },
  {
    kind: 'Bono 8',
    label: 'Diario de Bienestar de 30 Días',
    description:
      '8 páginas para registrar tus prácticas, emociones y observaciones durante 30 días.',
  },
];

export const valueSummary = [
  { value: '10', label: 'Archivos digitales' },
  { value: '97', label: 'Páginas visuales' },
  { value: '1', label: 'Guía principal' },
  { value: '8', label: 'Bonos complementarios' },
  { value: '♾️', label: 'Acceso de por vida' },
];

export const CHECKOUT_URL = 'https://aura-viva-2026.impultienda.ar/checkout';
